import { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { useLocation, useNavigate, Link } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'For Landlords', href: '#landlords' },
  { label: 'For Tenants', href: '#tenants' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'FAQ', href: '#faq' },
];

const SCROLL_THRESHOLD = 40;

function DesktopNav({ solid, onNavigate }) {
  return (
    <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
      {NAV_LINKS.map((link) => (
        <button
          key={link.href}
          onClick={() => onNavigate(link.href)}
          data-testid={`nav-${link.href.replace('#', '')}`}
          className={`font-body text-sm font-medium transition-colors duration-200 ${
            solid ? 'text-ink/80 hover:text-compliance-green' : 'text-white/85 hover:text-white'
          }`}
        >
          {link.label}
        </button>
      ))}
      <button
        onClick={() => onNavigate('#download')}
        data-testid="nav-download-cta"
        className="bg-compliance-green hover:bg-compliance-green/90 text-white font-body font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-compliance-green/25 active:scale-95"
      >
        Download App
      </button>
    </nav>
  );
}

function MobileMenu({ onNavigate }) {
  return (
    <div data-testid="mobile-menu" className="lg:hidden bg-white shadow-xl border-t border-gray-100 animate-fade-in">
      <div className="px-5 py-5 space-y-1">
        {NAV_LINKS.map((link) => (
          <button
            key={link.href}
            onClick={() => onNavigate(link.href)}
            data-testid={`mobile-nav-${link.href.replace('#', '')}`}
            className="block w-full text-left text-ink font-body text-[15px] font-medium py-2.5 px-3 rounded-lg hover:bg-mist transition-colors"
          >
            {link.label}
          </button>
        ))}
        <button
          onClick={() => onNavigate('#download')}
          data-testid="mobile-nav-download-cta"
          className="w-full mt-3 bg-compliance-green text-white font-body font-semibold text-[15px] px-5 py-3 rounded-full"
        >
          Download App
        </button>
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrolled]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollTo = useCallback((href) => {
    setMobileOpen(false);
    if (isHome) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + href);
    }
  }, [isHome, navigate]);

  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="ActCheck logo" className="h-9 w-auto" />
          <span className={`font-heading font-bold text-lg transition-colors duration-300 ${solid ? 'text-navy' : 'text-white'}`}>
            ActCheck
          </span>
        </Link>
        <DesktopNav solid={solid} onNavigate={scrollTo} />
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2"
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X size={24} className={solid ? 'text-ink' : 'text-white'} />
          ) : (
            <Menu size={24} className={solid ? 'text-ink' : 'text-white'} />
          )}
        </button>
      </div>
      {mobileOpen && <MobileMenu onNavigate={scrollTo} />}
    </header>
  );
}
